"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar as CalendarIcon, Clock, MapPin, Stethoscope, Loader as Loader2, CircleCheck as CheckCircle2, CircleAlert as AlertCircle } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
function AppointmentFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="mb-6 flex size-16 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30">
          <CheckCircle2 className="size-8 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="font-default mb-2 text-2xl font-semibold text-foreground">Request Received</h3>
        <p className="mb-8 max-w-md text-muted-foreground">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="min-w-[200px]">
          Book Another Appointment
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid gap-6 md:grid-cols-2">
        {}
        <div className="space-y-6 md:col-span-2">
          <div className="border-b border-border pb-2">
            <h4 className="font-default text-lg font-medium text-foreground">Patient Information</h4>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <FormField control={control} name="firstName" rules={{
            required: "First name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            <FormField control={control} name="lastName" rules={{
            required: "Last name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="john.doe@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            <FormField control={control} name="phone" rules={{
            required: "Phone number is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="(555) 123-4567" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        {}
        <div className="space-y-6 md:col-span-2">
          <div className="border-b border-border pb-2 pt-4">
            <h4 className="font-default text-lg font-medium text-foreground">Appointment Details</h4>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <FormField control={control} name="location" rules={{
            required: "Please select a preferred location"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Preferred Location</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <div className="flex items-center gap-2">
                          <MapPin className="size-4 text-muted-foreground" />
                          <SelectValue placeholder="Select a location" />
                        </div>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="delray">Delray Beach</SelectItem>
                      <SelectItem value="boynton">Boynton Beach</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />
            <FormField control={control} name="reason" rules={{
            required: "Please select a reason for visit"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Reason for Visit</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <div className="flex items-center gap-2">
                          <Stethoscope className="size-4 text-muted-foreground" />
                          <SelectValue placeholder="Select a reason" />
                        </div>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="routine">Routine checkup</SelectItem>
                      <SelectItem value="chest_pain">Chest pain</SelectItem>
                      <SelectItem value="shortness_breath">Shortness of breath</SelectItem>
                      <SelectItem value="follow_up">Follow-up</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />
            <FormField control={control} name="date" rules={{
            required: "Please select a preferred date"
          }} render={({
            field
          }) => <FormItem className="flex flex-col justify-end">
                  <FormLabel>Preferred Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button variant={"outline"} className={cn("w-full pl-3 text-left font-normal", !field.value && "text-muted-foreground")}>
                          {field.value ? field.value.toLocaleDateString() : <span>Pick a date</span>}
                          <SafeHydrate><CalendarIcon className="ml-auto size-4 opacity-50" /></SafeHydrate>
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <SafeHydrate><Calendar mode="single" selected={field.value} onSelect={field.onChange} disabled={date => date < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus /></SafeHydrate>
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>} />
            <FormField control={control} name="time" rules={{
            required: "Please select a preferred time"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Preferred Time</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <div className="flex items-center gap-2">
                          <Clock className="size-4 text-muted-foreground" />
                          <SelectValue placeholder="Select a time" />
                        </div>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="morning">Morning (8:00 AM - 12:00 PM)</SelectItem>
                      <SelectItem value="afternoon">Afternoon (12:00 PM - 4:00 PM)</SelectItem>
                      <SelectItem value="evening">Late Afternoon (4:00 PM - 6:00 PM)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        {}
        <div className="space-y-6 md:col-span-2">
          <div className="border-b border-border pb-2 pt-4">
            <h4 className="font-default text-lg font-medium text-foreground">Additional Information</h4>
          </div>
          <div className="grid gap-4">
            <FormField control={control} name="insurance" render={({
            field
          }) => <FormItem>
                  <FormLabel>Insurance Provider (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Blue Cross, Medicare, etc." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            <FormField control={control} name="notes" render={({
            field
          }) => <FormItem>
                  <FormLabel>Additional Notes or Questions</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Please share any specific symptoms or concerns..." className="min-h-[120px] resize-y" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>
      </div>

      {submitError && <div className="mt-6 flex items-center gap-3 rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-destructive">
          <AlertCircle className="size-5 shrink-0" />
          <p className="text-sm font-medium">{submitError}</p>
        </div>}

      <div className="mt-8 flex flex-col items-center justify-between gap-4 border-t border-border pt-6 sm:flex-row">
        <p className="text-sm text-muted-foreground">
          In case of a medical emergency, please call 911 immediately.
        </p>
        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full sm:w-auto min-w-[200px]">
          {isSubmitting ? <>
              <Loader2 className="mr-2 size-5 animate-spin" />
              Submitting Request...
            </> : 'Request Appointment'}
        </Button>
      </div>
    </Form>;
}
export default function AppointmentBookingForm() {
  return <section id="appointmentbookingform" className="bg-background py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mx-auto max-w-3xl text-center mb-12">
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Request an Appointment
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Schedule your visit with Florida Premier Cardiology. Please fill out the form below and our team will contact you shortly to confirm your appointment time.
          </p>
        </div>

        <div className="mx-auto max-w-4xl">
          <Card className="border-border/50 shadow-xl shadow-shadow-color/5">
            <CardHeader className="bg-muted/30 border-b border-border/50 pb-8 pt-8">
              <CardTitle className="font-default text-2xl">Booking Form</CardTitle>
              <CardDescription className="text-base">
                All fields marked with an asterisk (*) are required.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 md:p-8">
              <FormProvider formId="appointment-booking-form" sectionName="AppointmentBookingForm" defaultValues={{
              firstName: "",
              lastName: "",
              email: "",
              phone: "",
              location: "",
              reason: "",
              date: undefined,
              time: "",
              insurance: "",
              notes: ""
            }} submitText="Request Appointment" successMessage="Thank you for requesting an appointment. Our office will contact you within 1 business day to confirm your scheduled time.">
                <AppointmentFormInner />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}